import type { TradeItem } from '@/types';
import { getTradeExposure, getTradeTotalValue } from './trade-math';

/**
 * Settlement-currency display helpers (LR-474).
 *
 * Doctrine: money amounts display settlement-ccy primary; per-share prices stay
 * in the security currency (instrument quotation). The backend keeps all stored
 * amounts in the security currency and derives settlement twins server-side:
 * `*_converted = value × fx_rate` (booked trade rate, position.py `_to_settlement`),
 * null for same-currency trades. So the two display rules are:
 *
 *   currency: `settlement_currency ?? currency`   (absent ⇔ same-ccy)
 *   value:    `*_converted ?? native`             (null   ⇔ same-ccy ⇒ native correct)
 *
 * Everything `??`-shaped about settlement display must route through this
 * module — scattered fallbacks were how LR-459's twin columns drifted.
 *
 * Two FX sources exist and must not mix: the booked per-trade `fx_rate` (used
 * here — matches the backend's `*_converted` figures) and the live spot table
 * (`currency-service`, used only for the cross-currency ≈-roll-ups). A value
 * converted with one must never be compared or summed against the other.
 */

/** Currency the trade settles in; falls back to the security currency for
 *  same-ccy trades (and legacy payloads without the field). Uppercased —
 *  doubles as the bucket key for per-currency aggregation. */
export const getEffectiveSettlementCurrency = (t: {
  settlement_currency?: string | null;
  currency?: string | null;
}): string =>
  (t.settlement_currency || t.currency || '').toUpperCase();

/** True when the trade settles in a different currency than the security. */
export const isCrossCurrency = (t: TradeItem): boolean =>
  !!t.settlement_currency &&
  !!t.currency &&
  t.settlement_currency.toUpperCase() !== t.currency.toUpperCase();

/** Money metrics that have a backend-computed settlement twin (LR-459). */
export type SettlementMoneyField =
  | 'margin_price'
  | 'contract_value'
  | 'daily_earnings'
  | 'closing_cash'
  // LR-476: marked (post-MtM) display twins — same conversion path as their
  // booking counterparts, separate fields so the booking baseline is untouched.
  | 'marked_margin_price'
  | 'marked_contract_value'
  // LR-521: COB margin price + gross/margin values. Their *_converted twins are
  // computed server-side at the COB FX rate (fallback booked fx_rate), so the
  // same converted-first display rule applies; the degenerate native×fx fallback
  // below uses the booked rate (only reachable if the twin is unexpectedly null).
  | 'cob_margin_price'
  | 'cob_gross_value'
  | 'cob_margin_value'
  // LR-522: Trade Gross Value + Last Marked Gross Value. Backend-computed
  // settlement twins at the booked fx_rate (like margin_price/contract_value);
  // the gross PRICES they derive from stay in the security ccy, but these VALUES
  // convert. last_marked_gross_value is null until the position is first marked.
  | 'trade_gross_value'
  | 'last_marked_gross_value';

const NATIVE_ACCESSOR: Record<SettlementMoneyField, (t: TradeItem) => number | null | undefined> = {
  margin_price: (t) => t.margin_price,
  contract_value: (t) => t.contract_value,
  // LR-628: a floating rebate's daily figure is priced on today's fixing. The
  // module doctrine below says every `??`-shaped display rule routes through
  // here, so the effective-over-booked preference belongs in this one pair of
  // accessors rather than in each blotter cell and roll-up.
  daily_earnings: (t) => t.effective_daily_earnings ?? t.daily_earnings,
  closing_cash: (t) => t.closing_cash,
  marked_margin_price: (t) => t.marked_margin_price,
  marked_contract_value: (t) => t.marked_contract_value,
  cob_margin_price: (t) => t.cob_margin_price,
  cob_gross_value: (t) => t.cob_gross_value,
  cob_margin_value: (t) => t.cob_margin_value,
  trade_gross_value: (t) => t.trade_gross_value,
  last_marked_gross_value: (t) => t.last_marked_gross_value,
};

const CONVERTED_ACCESSOR: Record<SettlementMoneyField, (t: TradeItem) => number | null | undefined> = {
  margin_price: (t) => t.margin_price_converted,
  contract_value: (t) => t.contract_value_converted,
  daily_earnings: (t) => t.effective_daily_earnings_converted ?? t.daily_earnings_converted,
  closing_cash: (t) => t.closing_cash_converted,
  marked_margin_price: (t) => t.marked_margin_price_converted,
  marked_contract_value: (t) => t.marked_contract_value_converted,
  cob_margin_price: (t) => t.cob_margin_price_converted,
  cob_gross_value: (t) => t.cob_gross_value_converted,
  cob_margin_value: (t) => t.cob_margin_value_converted,
  trade_gross_value: (t) => t.trade_gross_value_converted,
  last_marked_gross_value: (t) => t.last_marked_gross_value_converted,
};

/**
 * Settlement-currency amount of a money metric: backend `*_converted` when
 * present (cross-ccy), otherwise the native value run through the booked
 * fx_rate (same-ccy passes through unchanged). Returns null when the metric
 * itself is missing. Pairs with `getEffectiveSettlementCurrency` as the bucket
 * key for aggregation. Degenerate case (cross-ccy legacy payload with neither
 * twin nor fx_rate) falls back to the raw native figure — cells use
 * `getSettlementDisplay` instead, which keeps the label honest there.
 */
export const getSettlementAmount = (t: TradeItem, field: SettlementMoneyField): number | null => {
  const converted = CONVERTED_ACCESSOR[field](t);
  if (converted != null && Number.isFinite(converted)) return converted;
  const native = NATIVE_ACCESSOR[field](t);
  if (native == null || !Number.isFinite(native)) return null;
  return toSettlementValue(t, native).value;
};

/** Native (security-ccy) counterpart of `getSettlementAmount`, for tooltips. */
export const getNativeAmount = (t: TradeItem, field: SettlementMoneyField): number | null => {
  const native = NATIVE_ACCESSOR[field](t);
  return native != null && Number.isFinite(native) ? native : null;
};

/**
 * Value + currency PAIR for a money cell — the two must never be picked
 * independently (a `converted ?? native` value next to an unconditional
 * `settlement_currency` label silently mislabels a native figure when the
 * conversion is missing/NaN on a cross-ccy trade). Resolution order:
 *   1. backend `*_converted` (cross-ccy) → settlement ccy;
 *   2. same-ccy native → its own currency (== effective settlement);
 *   3. cross-ccy without a usable twin → native × booked fx_rate;
 *   4. no fx either → the native figure honestly labelled with the SECURITY ccy.
 */
export const getSettlementDisplay = (
  t: TradeItem,
  field: SettlementMoneyField,
): { value: number; ccy: string } | null => {
  const converted = CONVERTED_ACCESSOR[field](t);
  if (converted != null && Number.isFinite(converted)) {
    return { value: converted, ccy: getEffectiveSettlementCurrency(t) };
  }
  const native = getNativeAmount(t, field);
  if (native == null) return null;
  if (!isCrossCurrency(t)) return { value: native, ccy: (t.currency || '').toUpperCase() };
  const fx = getBookedFxRate(t);
  if (fx != null) return { value: native * fx, ccy: getEffectiveSettlementCurrency(t) };
  return { value: native, ccy: (t.currency || '').toUpperCase() };
};

/** Booked FX rate (security → settlement); null when unusable. */
export const getBookedFxRate = (t: TradeItem): number | null =>
  t.fx_rate != null && Number.isFinite(t.fx_rate) && t.fx_rate > 0 ? t.fx_rate : null;

/** The COB FX rate as DISPLAYED in the blotter (LR-494): the market-pair value
 *  settlement/security (= 1 / stored security→settlement rate), with a
 *  COB→booked fallback. Single source of truth so the cell, copy and CSV
 *  export of this column never disagree. Returns null for same-ccy rows or when
 *  neither a COB nor a booked rate is usable (the cell renders a dash).
 *  `usingCob` is false when the value fell back to the booked fx_rate — the
 *  cell renders that amber and flags the tooltip as "unavailable". */
export interface DisplayedCobFx {
  /** Displayed pair value = 1 / (security→settlement rate). */
  value: number;
  /** True for a real COB rate; false for the booked-fx_rate fallback. */
  usingCob: boolean;
}
export const getDisplayedCobFxRate = (t: TradeItem): DisplayedCobFx | null => {
  if (!isCrossCurrency(t)) return null;
  const cob = t.cob_fx_rate;
  const usingCob = cob != null && Number.isFinite(cob) && cob > 0;
  const rate = usingCob ? cob! : getBookedFxRate(t); // security → settlement
  if (rate == null || rate <= 0) return null;
  return { value: 1 / rate, usingCob };
};

/** The booked FX rate as DISPLAYED in the blotter (LR-522 "Trade FX Rate"): the
 *  market pair settlement/security (= 1 / stored security→settlement booked
 *  rate), mirroring `getDisplayedCobFxRate` for the COB column. Single source of
 *  truth so the cell, copy and CSV export of the Trade FX Rate column
 *  agree. Returns null for same-ccy rows or when no usable booked rate exists
 *  (the cell renders a dash). */
export const getDisplayedBookedFxRate = (t: TradeItem): number | null => {
  if (!isCrossCurrency(t)) return null;
  const rate = getBookedFxRate(t); // security → settlement
  return rate == null || rate <= 0 ? null : 1 / rate;
};

export interface SettlementConversion {
  /** Amount in the effective settlement currency (best effort — see flag). */
  value: number;
  /** False when a cross-ccy trade had no usable booked fx_rate and the value
   *  stayed in the security currency. Callers aggregating into settlement
   *  buckets must surface this (⚠) — a silent native number under a
   *  settlement bucket key is a lie. */
  converted: boolean;
}

/**
 * Convert a native security-ccy amount into the settlement currency using the
 * booked trade rate (NOT live FX — keeps figures consistent with the
 * backend's `*_converted` family). Same-ccy trades pass through unchanged.
 */
export const toSettlementValue = (t: TradeItem, nativeValue: number): SettlementConversion => {
  if (!isCrossCurrency(t)) return { value: nativeValue, converted: true };
  const fx = getBookedFxRate(t);
  if (fx == null) return { value: nativeValue, converted: false };
  return { value: nativeValue * fx, converted: true };
};

/** Total Value (qty × mark / contract cash) in the settlement currency. */
export const getSettlementTotalValue = (t: TradeItem): SettlementConversion =>
  toSettlementValue(t, getTradeTotalValue(t));

/** Signed exposure (see trade-math sign convention) in the settlement currency. */
export const getSettlementExposure = (t: TradeItem): SettlementConversion =>
  toSettlementValue(t, getTradeExposure(t));

/**
 * HONEST bucket key for fx-converted computed amounts (Total Value, exposure,
 * recomputed marks): the settlement ccy when the booked fx_rate can actually
 * convert, the SECURITY ccy otherwise — i.e. the currency the figure produced
 * by `toSettlementValue` really is in. Pair with `toSettlementValue`-based
 * valueFns in `aggregateByCurrency`; pairing `getEffectiveSettlementCurrency`
 * with those valueFns instead silently labels an unconverted native figure
 * with the settlement ccy (LR-474 r1 finding #1).
 */
export const getSettlementConversionCcy = (t: TradeItem): string =>
  isCrossCurrency(t) && getBookedFxRate(t) == null
    ? (t.currency || '').toUpperCase()
    : getEffectiveSettlementCurrency(t);

/** Honest bucket key for twin-backed money metrics — the ccy half of
 *  `getSettlementDisplay` (settlement when the twin/fx converts, security on
 *  the degenerate fallback). Pair with `getSettlementAmount(t, field)`. */
export const getSettlementAmountCcy = (t: TradeItem, field: SettlementMoneyField): string =>
  getSettlementDisplay(t, field)?.ccy || getEffectiveSettlementCurrency(t);
